import React from "react";
import {
  FaMagic,
  FaRobot,
  FaShieldAlt,
  FaBolt,
  FaCheckCircle,
  FaCrown,
  FaRocket,
  FaArrowRight,
  FaGift,
  FaStar,
} from "react-icons/fa";
import { supabase } from "../supabaseClient";

const PricingPage = () => {
  const backendUrl = "https://jobzam-backend.onrender.com";

  const handleCheckout = async ({ priceId, coupon }) => {
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("❌ Supabase session error:", sessionError);
        alert("Session error. Please log in again.");
        window.location.href = "/login";
        return;
      }

      if (!session?.access_token) {
        alert("Please log in to upgrade.");
        window.location.href = "/login";
        return;
      }

      const response = await fetch(`${backendUrl}/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          price_id: priceId,
          coupon: coupon || null,
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error("❌ Checkout API failed:", response.status, errText);
        alert("Checkout failed. Please try again.");
        return;
      }

      const data = await response.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        console.error("❌ Missing checkout_url in response:", data);
        alert("Something went wrong. Try again.");
      }
    } catch (err) {
      console.error("❌ Checkout error:", err);
      alert("Checkout failed.");
    }
  };

  const premiumFeatures = [
    "Premium Resume Generator",
    "Optimized Resume Generation",
    "ATS Match & Score Reports",
    "AI Cover Letter Generator",
    "Interview Prep Toolkit",
  ];

  const comingSoonFeatures = [
    "Zam Agent — coming soon",
    "Resume Templates & PDF Themes — coming soon",
    "AI Job Board — coming soon",
    "UK Sponsorship Job Listings — coming soon",
  ];

  const FeatureRow = ({ children, muted = false }) => (
    <li className="flex items-start gap-3">
      <span className={`mt-1 ${muted ? "text-amber-500" : "text-green-500"}`}>
        {muted ? "✨" : <FaCheckCircle className="text-sm" />}
      </span>
      <span className={muted ? "text-gray-600" : "text-gray-700"}>{children}</span>
    </li>
  );

  return (
    <div className="relative overflow-hidden min-h-screen bg-slate-50 pt-10 pb-24 px-4">
      {/* Background glow effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-blue-200/30 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute top-40 right-[-120px] w-[320px] h-[320px] bg-yellow-200/30 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute bottom-20 left-[-100px] w-[280px] h-[280px] bg-purple-200/30 blur-3xl rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* HERO */}
        <section className="text-center max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50 px-5 py-2 text-sm font-bold text-yellow-700 shadow-sm">
            <FaGift />
            1st Anniversary Offer • 50% Off Premium
          </div>

          <h1 className="mt-6 text-4xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight">
            Celebrate 1 Year of{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              JobZam
            </span>
          </h1>

          <p className="mt-4 text-xl md:text-2xl font-semibold text-gray-800">
            Land better opportunities with our 1st anniversary premium offer
          </p>

          <p className="mt-5 text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Join over <span className="font-bold text-blue-600">15,000+ job seekers</span>{" "}
            who trust JobZam to build stronger resumes, better cover letters,
            and smarter job applications.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <div className="rounded-full bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm">
              Trusted by 15,000+ users
            </div>
            <div className="rounded-full bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm">
              No subscriptions
            </div>
            <div className="rounded-full bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm">
              One-time access only
            </div>
          </div>
        </section>

        {/* SPECIAL OFFER STRIP */}
        <section className="max-w-4xl mx-auto mt-10">
          <div className="rounded-[28px] border border-blue-100 bg-white/90 backdrop-blur-xl shadow-[0_10px_40px_rgba(59,130,246,0.12)] px-6 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                <FaCheckCircle className="text-green-600 text-xl" />
              </div>
              <div className="text-left">
                <p className="text-lg font-bold text-gray-900">
                  Trusted by <span className="text-blue-600">15,000+</span> job seekers worldwide
                </p>
                <p className="text-sm text-gray-600">
                  Anniversary pricing is now live for a limited time
                </p>
              </div>
            </div>

            <button
              onClick={() =>
                handleCheckout({
                  priceId: "price_1RPwtHEje0hcWx3u3mHWoCvD",
                })
              }
              className="rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 transition shadow-lg"
            >
              Claim 50% Off
            </button>
          </div>
        </section>

        {/* PRICING CARDS */}
        <section className="grid gap-8 lg:grid-cols-3 mt-14">
          {/* FREE */}
          <div className="rounded-[30px] border border-gray-200 bg-white/90 backdrop-blur p-8 shadow-lg hover:shadow-2xl transition duration-300 flex flex-col justify-between">
            <div>
              <div className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-700">
                Free Access
              </div>

              <h2 className="mt-5 text-3xl font-bold text-gray-900">Free</h2>
              <div className="mt-3 flex items-end gap-2">
                <span className="text-5xl font-extrabold text-gray-900">£0</span>
                <span className="text-lg text-gray-500 mb-1">forever</span>
              </div>

              <p className="mt-4 text-gray-600">
                Start free and explore JobZam before upgrading.
              </p>

              <ul className="mt-8 space-y-4">
                <FeatureRow>3 Free Resume Generations</FeatureRow>
                <FeatureRow>Unlimited ATS Match Scoring</FeatureRow>
                <li className="text-gray-400">✕ AI Cover Letter Generator</li>
                <li className="text-gray-400">✕ Interview Prep Toolkit</li>
                <li className="text-gray-400">✕ Premium Resume Generation</li>
                <li className="text-gray-400">✕ Zam Agent — AI Job Assistant</li>
                <li className="text-gray-400">✕ Job Board & Sponsorship Details</li>
              </ul>
            </div>

            <div className="mt-8">
              <button
                className="w-full rounded-2xl bg-gray-900 hover:bg-black text-white py-3.5 font-semibold transition"
                onClick={() => (window.location.href = "/upload")}
              >
                Start Free
              </button>
              <p className="mt-3 text-xs text-gray-500">
                Try 3 free resume generations before upgrading.
              </p>
            </div>
          </div>

          {/* 3 MONTH PREMIUM */}
          <div className="relative rounded-[34px] border-2 border-yellow-400 bg-gradient-to-b from-[#fff9e8] via-white to-[#fffdf7] p-8 shadow-[0_20px_80px_rgba(234,179,8,0.22)] hover:shadow-[0_25px_100px_rgba(234,179,8,0.28)] transition duration-300 flex flex-col justify-between scale-[1.02]">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-5 py-2 text-sm font-bold shadow-lg">
              MOST POPULAR
            </div>

            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-yellow-500 text-white px-4 py-1.5 text-sm font-bold shadow-sm mt-4">
                <FaCrown />
                1st Anniversary • 50% OFF
              </div>

              <h2 className="mt-5 text-3xl font-extrabold text-gray-900">
                3-Month Premium
              </h2>

              <div className="mt-4 flex items-end gap-3">
                <span className="text-2xl font-bold text-gray-400 line-through">£30</span>
                <span className="text-6xl font-extrabold text-gray-900">£15</span>
              </div>

              <p className="mt-2 text-base font-medium text-gray-600">
                One-time payment • 90 days access
              </p>

              <div className="mt-5 rounded-2xl bg-white border border-yellow-200 p-4 shadow-sm">
                <div className="flex items-center gap-2 text-gray-900 font-bold">
                  <FaStar className="text-yellow-500" />
                  Best value for serious job seekers
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Get full premium access at our special anniversary price.
                </p>
              </div>

              <ul className="mt-8 space-y-4">
                {premiumFeatures.map((feature) => (
                  <FeatureRow key={feature}>{feature}</FeatureRow>
                ))}
                {comingSoonFeatures.map((feature) => (
                  <FeatureRow key={feature} muted>
                    {feature}
                  </FeatureRow>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <button
                className="w-full rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-4 font-bold transition flex items-center justify-center gap-2 shadow-lg"
                onClick={() =>
                  handleCheckout({
                    priceId: "price_1RPwtHEje0hcWx3u3mHWoCvD",
                  })
                }
              >
                Claim Anniversary Offer <FaArrowRight />
              </button>

              <p className="mt-3 text-xs text-center text-gray-500">
                Limited-time pricing • No recurring subscription
              </p>
            </div>
          </div>

          {/* 30 DAY */}
          <div className="rounded-[30px] border border-green-200 bg-white/90 backdrop-blur p-8 shadow-lg hover:shadow-2xl transition duration-300 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-1.5 text-sm font-bold text-green-700">
                <FaBolt />
                Starter Premium
              </div>

              <h2 className="mt-5 text-3xl font-bold text-gray-900">
                30-Day Premium
              </h2>

              <div className="mt-4 flex items-end gap-3">
                <span className="text-2xl font-bold text-gray-400 line-through">£9.99</span>
                <span className="text-6xl font-extrabold text-gray-900">£5.99</span>
              </div>

              <p className="mt-2 text-base font-medium text-gray-600">
                One-time payment • 30 days access
              </p>

              <div className="mt-5 rounded-2xl bg-green-50 border border-green-100 p-4">
                <p className="font-bold text-gray-900">Great for quick job search sprints</p>
                <p className="text-sm text-gray-600 mt-1">
                  A lower entry price for users who want premium access right now.
                </p>
              </div>

              <ul className="mt-8 space-y-4">
                {premiumFeatures.map((feature) => (
                  <FeatureRow key={feature}>{feature}</FeatureRow>
                ))}
                {comingSoonFeatures.map((feature) => (
                  <FeatureRow key={feature} muted>
                    {feature}
                  </FeatureRow>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <button
                className="w-full rounded-2xl bg-green-600 hover:bg-green-700 text-white py-4 font-bold transition flex items-center justify-center gap-2"
                onClick={() =>
                  handleCheckout({
                    priceId: "price_1SYSe4Eje0hcWx3uO6dX4wW3",
                  })
                }
              >
                Get 30-Day Access <FaArrowRight />
              </button>

              <p className="mt-3 text-xs text-center text-gray-500">
                One-time payment • No hidden renewal
              </p>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE */}
        <section className="mt-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
              Why users choose JobZam
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold text-gray-900">
              Everything you need to apply smarter
            </h2>
            <p className="mt-4 text-gray-600">
              JobZam helps you stand out with AI-powered tools built for modern job seekers.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="rounded-[28px] bg-white p-8 shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center mb-5">
                <FaMagic className="text-purple-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                All-in-One Career Platform
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Resume optimization, ATS scoring, cover letters, and interview prep
                in one powerful platform.
              </p>
            </div>

            <div className="rounded-[28px] bg-white p-8 shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-5">
                <FaRobot className="text-blue-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Smart AI-Powered Tools
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Tailor applications in minutes and create stronger job materials faster.
              </p>
            </div>

            <div className="rounded-[28px] bg-white p-8 shadow-lg border border-gray-100 hover:shadow-xl transition">
              <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mb-5">
                <FaShieldAlt className="text-green-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Simple One-Time Pricing
              </h3>
              <p className="text-gray-600 leading-relaxed">
                No auto-renewals. No surprise subscription charges. Just simple premium access.
              </p>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mt-20 max-w-5xl mx-auto">
          <div className="rounded-[36px] bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-12 text-white text-center shadow-[0_20px_80px_rgba(79,70,229,0.35)]">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold">
              <FaRocket />
              1st Anniversary Special
            </div>

            <h3 className="mt-5 text-3xl md:text-5xl font-extrabold">
              Ready to upgrade your job search?
            </h3>

            <p className="mt-4 text-blue-100 max-w-2xl mx-auto text-base md:text-lg">
              Join 15,000+ users already using JobZam and unlock premium tools
              with our limited-time 50% anniversary offer.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <button
                className="rounded-2xl bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 font-bold transition shadow-lg"
                onClick={() =>
                  handleCheckout({
                    priceId: "price_1RPwtHEje0hcWx3u3mHWoCvD",
                  })
                }
              >
                Get 50% Off Premium
              </button>

              <button
                className="rounded-2xl bg-white/10 hover:bg-white/20 border border-white/20 px-8 py-4 font-bold transition"
                onClick={() => (window.location.href = "/upload")}
              >
                Try Free First
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            JobZam® is a registered UK trademark (UK00004249682).
          </p>
        </section>
      </div>
    </div>
  );
};

export default PricingPage;
